import { pushRoute } from '../../lib/router.js';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, Checkbox } from 'react-bootstrap';
import { getOrgTermsContent } from '../../content/utils.js';
import AuthorizedAction from '../AuthorizedAction.js';
import SubHeader from '../SubHeader.jsx';
import Chart from './Chart.js';
import { PlusButtonIcon } from '../icons/PlusButtonIcon.jsx';
import { getOrgSlug } from '../../lib/utils.js';
import styles from './index.module.css';
import withFullscreen from '../common/withFullscreen.jsx';
import moment from 'moment';
import CheckboxAdapter from '../CheckboxAdapter.jsx';

const ACTIVITY_TYPE = 'activity';

const isCompleted = (initiative) => moment().isAfter(initiative.endDate, 'day');

class Timeline extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      startDate: Date.now(),
    };

    this.chart = null;

    this.addItemClickHandler = this.addItemClickHandler.bind(this);
    this.resizeChart = this.resizeChart.bind(this);
    this.current = this.current.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
  }

  componentDidMount() {
    const options = {
      width: document.body.clientWidth,
      padding: 42,
      height: 300,
      barHeight: 16,
      weekCount: 5,
      projectId: this.props.project.id,
      startDate: this.state.startDate,
      minHeight: 300,
    };
    const { onShowEditMilestone } = this.props;
    this.chart = new Chart(
      '#timeline',
      this.timelineItems,
      options,
      this.milestones,
      onShowEditMilestone
    );
    window.addEventListener('resize', this.resizeChart);

    this.chart.build();

    // rebuild chart with adapted height
    if (this.chart.rows) {
      if (this.chart.rows.length > 16) {
        this.chart.options.height = this.chart.rows.length * 20;
        this.chart.rebuild();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeChart);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.project !== this.props.project ||
      prevProps.showCompleted !== this.props.showCompleted
    ) {
      this.chart.data = this.timelineItems;
      this.chart.milestones = this.milestones;
      this.chart.rebuild();
    }
  }

  get timelineItems() {
    const filteredByDraftStatus = (arr) =>
      arr.filter((initiative) => !initiative.draft);

    const {
      activities,
      communicationInitiatives,
      readinessInitiatives,
      trainingInitiatives,
      sustainmentInitiatives,
    } = this.props.project;

    return [
      ...activities.map((a) => ({ ...a, type: ACTIVITY_TYPE })),
      ...filteredByDraftStatus(communicationInitiatives),
      ...filteredByDraftStatus(readinessInitiatives),
      ...filteredByDraftStatus(trainingInitiatives),
      ...filteredByDraftStatus(sustainmentInitiatives),
    ]
      .filter((i) => this.props.showCompleted || !isCompleted(i))
      .map(this.addItemClickHandler);
  }

  get milestones() {
    const { changeStrategy } = this.props;

    return changeStrategy && changeStrategy.milestones
      ? changeStrategy.milestones
      : [];
  }

  addItemClickHandler(item) {
    return {
      ...item,
      onClick: () => {
        if (item.type === ACTIVITY_TYPE) {
          this.props.onClickActivity(item.id);
        } else {
          pushRoute(`/${getOrgSlug()}/${item.type}/${item.id}`);
        }
      },
    };
  }

  resizeChart() {
    this.chart.options.width = document.body.clientWidth;
    this.chart.rebuild();
  }

  current() {
    this.setState({ startDate: Date.now() }, () => {
      this.chart.options.startDate = this.state.startDate;
      this.chart.update();
    });
  }

  prev() {
    this.setState(
      ({ startDate }) => ({ startDate: d3.timeWeek.offset(startDate, -2) }),
      () => {
        this.chart.options.startDate = this.state.startDate;
        this.chart.update();
      }
    );
  }

  next() {
    this.setState(
      ({ startDate }) => ({ startDate: d3.timeWeek.offset(startDate, 2) }),
      () => {
        this.chart.options.startDate = this.state.startDate;
        this.chart.update();
      }
    );
  }

  render() {
    const { isFullscreen, showCompleted, onToggleShowCompleted } = this.props;

    return (
      <section className={isFullscreen ? 'fullscreen' : undefined}>
        <SubHeader flexJustify small style={{ borderBottom: 'none' }}>
          <div className={`flex ${styles.flex}`}>
            <h2>Timeline</h2>
            <div className="pager header-item">
              <button className="page prev" onClick={this.prev}>
                Previous
              </button>
              <button className="page today" onClick={this.current}>
                Today
              </button>
              <button className="page next" onClick={this.next}>
                Next
              </button>
            </div>
          </div>
          <div className="flex flex-align-center" style={{ gap: '16px' }}>
            <div>
              <CheckboxAdapter
                checked={isFullscreen}
                onChange={this.props.onToggleFullscreen}
                label="Fullscreen"
                customClass="font-size-14 p-r-16"
                right
              />
            </div>
            {this.props.project.manageProjectEntities ? (
              <AuthorizedAction action="create" subject="activity">
                <CheckboxAdapter
                  checked={showCompleted}
                  onChange={(e) => onToggleShowCompleted(e.target.checked)}
                  label="Show Completed"
                  customClass="font-size-14 p-r-16"
                  right
                />
                <Button
                  bsStyle="success"
                  className="add-activity-button"
                  onClick={() => {
                    if (this.props.project.archived) {
                      window.alert(
                        getOrgTermsContent('archivedProjectMessage')
                      );
                    } else {
                      this.props.onAddActivity();
                    }
                  }}
                >
                  <PlusButtonIcon />
                  Add {getOrgTermsContent('projectActivity')}
                </Button>
              </AuthorizedAction>
            ) : null}
          </div>
        </SubHeader>
        <div
          className="component component-bg-white"
          style={{
            height: isFullscreen ? 'calc(100vh - 60px)' : '300px',
            overflowY: 'scroll',
            overflowX: 'hidden',
            padding: 0,
            paddingBottom: '150px',
            background: '#fff',
          }}
        >
          <div className="chartWrapper">
            <div id="timeline" />
            <div id="popover" />
            <div id="milestone-card"></div>
            <div id="tooltip">
              <div id="type" />
              <h3 id="title" />
              <strong id="category" />
            </div>
          </div>
        </div>
      </section>
    );
  }
}

const timelineItemPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  startDate: PropTypes.object.isRequired,
  endDate: PropTypes.object.isRequired,
});

Timeline.propTypes = {
  onAddActivity: PropTypes.func.isRequired,
  onClickActivity: PropTypes.func.isRequired,
  isFullscreen: PropTypes.bool.isRequired,
  onToggleFullscreen: PropTypes.func.isRequired,
  showCompleted: PropTypes.bool.isRequired,
  onToggleShowCompleted: PropTypes.func.isRequired,
  changeStrategy: PropTypes.any,
  milestones: PropTypes.any,
  project: PropTypes.shape({
    activities: PropTypes.arrayOf(timelineItemPropTypes).isRequired,
    communicationInitiatives: PropTypes.arrayOf(timelineItemPropTypes)
      .isRequired,
    readinessInitiatives: PropTypes.arrayOf(timelineItemPropTypes).isRequired,
    trainingInitiatives: PropTypes.arrayOf(timelineItemPropTypes).isRequired,
  }).isRequired,
};
export default withFullscreen(Timeline);
